import { Injectable } from '@angular/core';
import { SearchFilterSortService } from 'ng-searchfiltersort';
import ArrayStore from 'devextreme/data/array_store';
import DataSource from 'devextreme/data/data_source';
import { ProfileStoreService } from '../profile-store/profile-store.service';
import { SelectionStats } from './selection-stats';
import { AttributesEntity } from '../../models/configuration.model';
import { DataStoreService } from '../../core/services/data-store/data-store.service';
import { DictionaryStoreService } from '../dictionary-store/dictionary-store.service';

@Injectable()
export class ReceiverSelectionStoreService {
  private readonly maximumRecentReceivers = 10;
  private readonly receiversPageSize = 8;
  private selectedReceiverIdValue: string;
  private receiverStore: ArrayStore;
  private receiverSource: DataSource;
  private currentAttribute: AttributesEntity;
  private recentReceiversMode = false;
  readonly fetchAllMarkerId = '_WCZYTAJ_WSZYSTKO_';
  readonly fetchAllMarkerName = 'Wczytaj wszystko...';

  constructor(
    private profileStore: ProfileStoreService,
    private storeService: DataStoreService,
    private dictionaryStore: DictionaryStoreService,
    private sfsService: SearchFilterSortService
  ) {
    this.receiverStore = new ArrayStore();

    this.receiverSource = new DataSource({
      store: this.receiverStore,
      pageSize: this.receiversPageSize,
      paginate: true
    });
  }

  get selectedReceiverId(): string {
    return this.selectedReceiverIdValue;
  }

  get receivers(): DataSource {
    return this.receiverSource;
  }

  get isRecentReceiversMode(): boolean {
    return this.recentReceiversMode;
  }

  loadRecentReceivers(attribute?: AttributesEntity): Promise<void> {
    return new Promise(resolve => {
      if (attribute) {
        this.currentAttribute = attribute;
      }
      this.loadReceiversUsingRecent(this.currentAttribute);
      resolve();
    });
  }

  loadAllReceivers(): Promise<void> {
    return this.loadReceiversWithFilter(this.currentAttribute);
  }

  addRecentlySelectedReceiver(selectedReceiver: string, increase = 1) {
    this.restoreModifyAndStoreReceiverStats(receiverStats => {
      const selectedStats = receiverStats.find(s => s.value === selectedReceiver);
      if (selectedStats) {
        selectedStats.count += increase;
      } else {
        receiverStats.push({
          value: selectedReceiver,
          count: 1
        });
      }
      receiverStats = this.sfsService.orderBy(receiverStats, 'count', true);
      while (receiverStats.length > this.maximumRecentReceivers) {
        receiverStats.pop();
      }
      return receiverStats;
    });
  }

  removeRecentlySelectedReceiver(selectedReceiver: string): Promise<void> {
    return this.restoreModifyAndStoreReceiverStats(receiverStats => {
      const index = receiverStats.findIndex(s => s.value === selectedReceiver);
      if (index >= 0) {
        receiverStats.splice(index, 1);
      }
      return receiverStats;
    });
  }

  clearValues() {
    this.selectedReceiverIdValue = null;
  }

  private restoreModifyAndStoreReceiverStats(
    alterStats: (receiverStats: SelectionStats[]) => SelectionStats[]
  ): Promise<void> {
    return new Promise<void>(resolve => {
      this.profileStore.restoreSelectionStats(this.currentAttribute.id).subscribe(result => {
        let receiverStats: SelectionStats[] = [];
        if (result) {
          receiverStats = JSON.parse(result);
        }

        receiverStats = alterStats(receiverStats);

        this.profileStore
          .storeSelectionStats(this.currentAttribute.id, JSON.stringify(receiverStats))
          .subscribe(() => {
            resolve();
          });
      });
    });
  }

  private loadReceiversUsingRecent(attribute: AttributesEntity) {
    this.profileStore.restoreSelectionStats(this.currentAttribute.id).subscribe(
      result => {
        let loadAll = true;
        if (result) {
          const stats: SelectionStats[] = JSON.parse(result);
          if (stats && stats.length > 0) {
            const predicate = id => stats.findIndex(s => s.value === id) >= 0;
            this.loadReceiversWithFilter(attribute, predicate);
            loadAll = false;
          }
        }

        if (loadAll) {
          this.loadReceiversWithFilter(attribute);
        }
      },
      error => this.loadReceiversWithFilter(attribute)
    );
  }

  private loadReceiversWithFilter(
    attribute: AttributesEntity,
    predicate?: (id: string) => boolean
  ): Promise<void> {
    return new Promise(resolve => {
      this.receiverStore.clear();
      this.dictionaryStore
        .getDictionary(
          attribute.businessModelElementAttrId,
          attribute.value,
          this.storeService.currentElementId,
          this.storeService.currentElementViewId
        )
        .subscribe(result => {
          attribute.dirValue = result;
          for (const item of result) {
            if (!predicate || predicate(item.Id)) {
              this.receiverStore.insert(item);
            }
          }
          if (predicate) {
            this.receiverStore.insert({
              Id: this.fetchAllMarkerId,
              Name: this.fetchAllMarkerName,
              Description: null
            });
          }
          this.receiverSource.pageIndex(0);
          this.receiverSource.reload();
          resolve();
        });
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/services/receiver-selection-store/receiver-selection-store.service.ts